/**
 * Einstieg und Spielschleife — und sonst nichts.
 *
 * Die Simulation läuft mit festen 30 Hz aus einem Akkumulator, der Renderer mit
 * `requestAnimationFrame` und interpoliert zwischen den letzten beiden
 * Schnappschüssen.
 *
 * Alles Anzeigbare steckt in `hud/`, alles Zeichnende in `zeichnen/`. Diese
 * Datei hält nur die Schleife und den Zustand des laufenden Abstiegs — sie soll
 * nicht wachsen, wenn Anzeigen dazukommen (Ueberarbeitung A5).
 */

import type { EffektSicht, Schnappschuss, WesenSicht } from '@pg/protocol';
import { TAKTE_PRO_SEKUNDE } from '@pg/protocol';
import { Lauf, standardFreischaltungen, type Karte } from '@pg/sim';
import { ladeInhalte } from '@pg/content';
import { Steuerung } from './steuerung.js';
import { beuteUnterPunkt, Szene, type Kamera } from './zeichnen/szene.js';
import type { Farben } from './zeichnen/figuren.js';
import { HUD, hinweis, overlayOffen, setzeErtrag, type HudUmgebung } from './hud/index.js';
import { einstellungen, setzeZoom, ZOOMSTUFEN } from './einstellungen.js';

const TAKT_MS = 1000 / TAKTE_PRO_SEKUNDE;

const farben: Farben = {
  tinte: '#191512',
  papier: '#EFE7D6',
  flaeche: '#D9CDB4',
  schatten: '#B9A98A',
  glut: '#D4471C',
  messing: '#8A6A20',
  span: '#3A5A6B',
  fels: '#221C16',
  wahrzeichen: '#E3D8BF',
};

const brett = document.querySelector<HTMLCanvasElement>('#brett')!;
const spielFlaeche = document.querySelector<HTMLElement>('#spiel')!;
const stickAnzeige = document.querySelector<SVGSVGElement>('#stick')!;
const ctx = brett.getContext('2d', { alpha: false })!;

const inhalte = ladeInhalte();
const szene = new Szene(farben);
// Die Steuerung horcht NUR auf der Spielflaeche. Die Oberflaeche darueber
// behaelt ihre Ereignisse (Ueberarbeitung A1).
const steuerung = new Steuerung(brett);

let lauf: Lauf;
let karte: Karte;
let besucht: Uint8Array;
let vorher: Schnappschuss;
let jetzt: Schnappschuss;
let effekte: EffektSicht[] = [];
let akku = 0;
let letzteZeit = 0;
let laufzeit = 0;
const kamera: Kamera = { x: 0, y: 0, masstab: 40 };

function neuerSeed(): string {
  // Math.random ist im Client erlaubt — verboten ist es nur in @pg/sim.
  return Math.floor(Math.random() * 0xffffffff).toString(36);
}

const umgebung: HudUmgebung = {
  steuerung,
  karte: () => karte,
  besucht: () => besucht,
  neuerAbstieg: () => starte(),
};

/**
 * Startet einen Abstieg. Beim ersten Aufruf einen neuen Charakter, danach mit
 * dem Fortschritt des vorigen — bei Erfolg eine Tiefe tiefer, sonst dieselbe.
 */
function starte(): void {
  const alt = jetzt?.spieler;
  const geschafft = jetzt?.abstieg.stand === 'geschafft';
  const tiefe = alt ? Math.max(1, Math.min(30, jetzt.abstieg.tiefe + (geschafft ? 1 : 0))) : 1;
  const stufe = alt?.stufe ?? 1;

  lauf = new Lauf({
    seed: neuerSeed(),
    tiefe,
    grad: 1,
    stufe,
    erfahrung: alt?.erfahrung ?? 0,
    freigeschaltet: alt?.freigeschaltet ?? standardFreischaltungen(stufe, 'kesselschmied', inhalte),
    handbetrieb: einstellungen().handbetrieb,
    automatikgrad: einstellungen().automatikgrad,
    tempoprofil: einstellungen().tempoprofil,
  });

  karte = lauf.karte();
  besucht = new Uint8Array(karte.breite * karte.hoehe);
  vorher = lauf.schnappschuss();
  jetzt = vorher;
  effekte = [];
  akku = 0;
  laufzeit = 0;
  kamera.x = jetzt.spieler.pos.x;
  kamera.y = jetzt.spieler.pos.y;

  for (const teil of HUD) teil.bauen?.(umgebung, jetzt);
  merkeBesucht();
}

/** Sichtradius um den Spieler als besucht markieren — Grundlage des Nebels. */
function merkeBesucht(): void {
  const r = 8;
  const px = Math.floor(jetzt.spieler.pos.x);
  const py = Math.floor(jetzt.spieler.pos.y);
  for (let y = py - r; y <= py + r; y++) {
    if (y < 0 || y >= karte.hoehe) continue;
    for (let x = px - r; x <= px + r; x++) {
      if (x < 0 || x >= karte.breite) continue;
      if ((x - px) ** 2 + (y - py) ** 2 > r * r) continue;
      besucht[y * karte.breite + x] = 1;
    }
  }
}

function passeGroesseAn(): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const b = spielFlaeche.clientWidth;
  const h = spielFlaeche.clientHeight;
  brett.width = Math.round(b * dpr);
  brett.height = Math.round(h * dpr);
  brett.style.width = `${b}px`;
  brett.style.height = `${h}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  setzeMasstab();
}

/**
 * Zoom aus den Einstellungen. Einheiten Bildbreite (hochkant) bzw. -höhe (quer).
 *
 * Nach der Rückmeldung einstellbar gemacht (Ueberarbeitung B4): Der erste
 * Wurf stand fest auf „nah", weil die Figuren davor zu klein waren — das war
 * dann zu viel des Guten.
 */
function setzeMasstab(): void {
  const b = spielFlaeche.clientWidth;
  const h = spielFlaeche.clientHeight;
  const stufen = inhalte.regelwerk.ansicht.zoomstufen[einstellungen().zoom]!;
  kamera.masstab = h > b ? Math.max(26, b / stufen.hochkant) : Math.max(26, h / stufen.quer);
}

window.addEventListener('resize', passeGroesseAn);
window.addEventListener('orientationchange', () => setTimeout(passeGroesseAn, 120));

// Zwei-Finger-Zoom und Mausrad gehen über dieselbe Einstellung wie der Knopf.
steuerung.beiZoom((raus) => {
  const i = ZOOMSTUFEN.indexOf(einstellungen().zoom);
  const neu = ZOOMSTUFEN[Math.max(0, Math.min(ZOOMSTUFEN.length - 1, i + (raus ? 1 : -1)))]!;
  if (neu === einstellungen().zoom) return;
  setzeZoom(neu);
  setzeMasstab();
  hinweis(`Sicht: ${neu}`);
});

// Kurzer Tipp auf die Spielfläche: Beute aufheben. Beute wird angeklickt,
// nicht im Vorbeigehen genommen — ein Fund soll ein Ereignis sein.
steuerung.beiTipp((x, y) => {
  const r = brett.getBoundingClientRect();
  const bx = x - r.left;
  const by = y - r.top;

  // Beute hat Vorrang vor dem Förderkorb — sonst fährt man versehentlich hoch,
  // während man das letzte Stück aufheben wollte.
  const id = beuteUnterPunkt(jetzt, kamera, r.width, r.height, bx, by);
  if (id !== null) {
    steuerung.hebeAuf(id);
    return;
  }

  const a = jetzt.abstieg.ausgang;
  if (a && jetzt.abstieg.ausgangInReichweite) {
    const ax = (a.x - kamera.x) * kamera.masstab + r.width / 2;
    const ay = (a.y - kamera.y) * kamera.masstab + r.height / 2;
    if (Math.hypot(bx - ax, by - ay) < Math.max(60, kamera.masstab * 1.2)) steuerung.steigeEin();
  }
});

// ------------------------------------------------------------------ Interpolation

function mische(a: Schnappschuss, b: Schnappschuss, t: number): Schnappschuss {
  const nachId = new Map<number, WesenSicht>();
  for (const w of a.wesen) nachId.set(w.id, w);

  const wesen = b.wesen.map((w) => {
    const alt = nachId.get(w.id);
    if (!alt) return w;
    return {
      ...w,
      pos: { x: alt.pos.x + (w.pos.x - alt.pos.x) * t, y: alt.pos.y + (w.pos.y - alt.pos.y) * t },
    };
  });

  return {
    ...b,
    wesen,
    spieler: {
      ...b.spieler,
      pos: {
        x: a.spieler.pos.x + (b.spieler.pos.x - a.spieler.pos.x) * t,
        y: a.spieler.pos.y + (b.spieler.pos.y - a.spieler.pos.y) * t,
      },
    },
  };
}

// ------------------------------------------------------------------ Schleife

function bild(zeit: number): void {
  requestAnimationFrame(bild);
  if (letzteZeit === 0) letzteZeit = zeit;
  const delta = Math.min(250, zeit - letzteZeit);
  letzteZeit = zeit;
  laufzeit += delta / 1000;

  // Overlay offen? Dann steht die Welt. Wer eine Fähigkeit wählt oder den
  // Abschluss liest, soll dabei nicht geschlagen werden.
  const pausiert = overlayOffen();
  if (pausiert) akku = 0;
  else akku += delta;
  let takte = 0;
  // Höchstens 4 Takte pro Bild nachholen — sonst friert ein zurückgeholtes
  // Telefon sekundenlang ein, während es aufholt.
  while (!pausiert && akku >= TAKT_MS && takte < 4) {
    akku -= TAKT_MS;
    takte++;
    vorher = jetzt;
    const ereignisse = lauf.takt(steuerung.absichten());
    jetzt = lauf.schnappschuss();
    for (const e of jetzt.effekte) effekte.push(e);
    for (const e of ereignisse) {
      for (const teil of HUD) teil.ereignis?.(umgebung, e, jetzt);
    }
    merkeBesucht();
  }
  if (akku > TAKT_MS * 4) akku = 0;

  if (effekte.length > 220) effekte = effekte.slice(-220);
  effekte = effekte.filter((e) => jetzt.takt - e.takt < 48);

  const t = Math.min(1, akku / TAKT_MS);
  const sicht = mische(vorher, jetzt, t);

  // Massstab jedes Bild neu aus den Einstellungen ableiten. Zwei Divisionen,
  // dafuer kann er nicht mehr auseinanderlaufen: Vorher aenderte der
  // Zoom-Knopf die Einstellung, aber die Kamera las sie erst beim naechsten
  // Groessenwechsel — der Knopf schien wirkungslos.
  setzeMasstab();
  kamera.x += (sicht.spieler.pos.x - kamera.x) * Math.min(1, delta / 90);
  kamera.y += (sicht.spieler.pos.y - kamera.y) * Math.min(1, delta / 90);

  szene.zeichne(
    ctx,
    sicht,
    kamera,
    spielFlaeche.clientWidth,
    spielFlaeche.clientHeight,
    laufzeit,
    effekte,
    karte,
  );

  const ertrag = lauf.ertrag();
  setzeErtrag(ertrag.gold, ertrag.stuecke);
  for (const teil of HUD) teil.aktualisieren?.(umgebung, sicht);
  zeichneStick();
  setzePruefhaken(sicht);
}

function zeichneStick(): void {
  const st = steuerung.stick;
  if (!st.aktiv) {
    stickAnzeige.classList.remove('aktiv');
    return;
  }
  stickAnzeige.classList.add('aktiv');
  stickAnzeige.style.left = `${st.ursprung.x - 80}px`;
  stickAnzeige.style.top = `${st.ursprung.y - 80}px`;
  const dx = st.jetzt.x - st.ursprung.x;
  const dy = st.jetzt.y - st.ursprung.y;
  const l = Math.min(52, Math.hypot(dx, dy));
  const w = Math.atan2(dy, dx);
  const knopf = stickAnzeige.querySelector('.knopf') as SVGCircleElement;
  knopf.setAttribute('cx', String(80 + Math.cos(w) * l));
  knopf.setAttribute('cy', String(80 + Math.sin(w) * l));
}

/**
 * Prüfhaken für automatisierte Browsertests (Ueberarbeitung A7).
 * Schreibgeschützter Auszug — kein Weg, von außen in das Spiel zu greifen.
 */
function setzePruefhaken(s: Schnappschuss): void {
  (window as unknown as { __spiel: unknown }).__spiel = Object.freeze({
    pos: { x: s.spieler.pos.x, y: s.spieler.pos.y },
    leben: s.spieler.leben,
    lebenMax: s.spieler.lebenMax,
    stufe: s.spieler.stufe,
    tiefe: s.abstieg.tiefe,
    stand: s.abstieg.stand,
    autolauf: s.spieler.autolauf,
    anker: s.spieler.anker,
    automatikgrad: s.spieler.automatikgrad,
    tempoprofil: s.spieler.tempoprofil,
    slots: s.spieler.slots.map((x) => ({ name: x.name, handbetrieb: x.handbetrieb })),
    gegner: s.wesen.filter((w) => w.art === 'gegner').length,
    // Zum Pruefen: liegt anklickbare Beute herum, und ist der Foerderkorb unten?
    beuteAmBoden: s.beute.filter((x) => x.anklicken).length,
    ausgangOffen: s.abstieg.ausgang !== null,
    takt: s.takt,
  });
}

passeGroesseAn();
starte();
requestAnimationFrame(bild);
